import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { Link, useHistory } from 'react-router-dom';

import {
  AccountTable,
  DashboardError,
  DashboardPage,
  IAccountTableColumn,
} from '../../components/JobsAccount';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { book } from '../../routers/books';
import { useGetJobsPrepaidQuery } from '../../services/serviceJobsAccount';
import { IJobsPrepaidPackage } from '../../services/serviceJobsAccount/interfaces';
import {
  getPostPrepaidJobPath,
  getPrepaidPurchasePath,
} from '../../utils/jobAccountRoutes';
import { isForbidden } from '../../utils/jobAccessError';

const formatDate = (date: string | null): string => (
  date ? moment(date).format('MM/DD/YYYY') : '—'
);

export const JobsPrepaid = () => {
  const history = useHistory();
  const { data, error, isLoading, isFetching } = useGetJobsPrepaidQuery();

  const columns = (canPost: boolean): IAccountTableColumn<IJobsPrepaidPackage>[] => {
    const result: IAccountTableColumn<IJobsPrepaidPackage>[] = [
      {
        id: 'package',
        label: 'Prepaid Job Package',
        render: (pkg) => (
          <Button
            variant="text"
            onClick={() => history.push(book.jobsPrepaidDetail(pkg.prepaidGuid))}
            sx={{ textAlign: 'left' }}
          >
            #{pkg.prepaidId}: {pkg.company}
          </Button>
        ),
      },
      {
        id: 'type',
        label: 'Site',
        render: (pkg) => pkg.type || 'CPG Jobs',
      },
      {
        id: 'listingType',
        label: 'Listing Type',
        render: (pkg) => (pkg.isFeatured ? 'Featured' : 'Standard'),
      },
      {
        id: 'created',
        label: 'Created',
        render: (pkg) => formatDate(pkg.dateCreated),
      },
      {
        id: 'expires',
        label: 'Expires',
        render: (pkg) => formatDate(pkg.expirationDate),
      },
      {
        id: 'remaining',
        label: 'Remaining',
        align: 'center',
        render: (pkg) => `${pkg.remainingCount} / ${pkg.totalCount}`,
      },
    ];

    if (canPost) {
      result.push({
        id: 'post',
        label: 'Use Credit',
        align: 'right',
        render: (pkg) => (
          <Button
            variant="contained"
            size="small"
            onClick={() => history.push(getPostPrepaidJobPath(pkg.siteId, pkg.prepaidGuid))}
          >
            Use Listing Credit
          </Button>
        ),
      });
    }

    return result;
  };

  const denied = isForbidden(error);

  return (
    <DashboardPage
      title="Prepaid Job Packages"
      noAccessComponent={denied ? <NoAccessMessage /> : undefined}
      actions={(
        <Button
          component={Link}
          to={getPrepaidPurchasePath(null)}
          variant="contained"
        >
          Buy a Job Package
        </Button>
      )}
    >
      <DashboardError error={error} />

      <Stack spacing={4}>
        <section>
          <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
            Active Packages
          </Typography>
          <AccountTable
            ariaLabel="Active prepaid job packages"
            columns={columns(true)}
            rows={data?.currentPackages || []}
            getRowId={(pkg) => pkg.prepaidId}
            emptyMessage="You do not have any active prepaid job packages."
            isLoading={isLoading || isFetching}
          />
        </section>

        <section>
          <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
            Completed / Expired Packages
          </Typography>
          <AccountTable
            ariaLabel="Completed prepaid job packages"
            columns={columns(false)}
            rows={data?.completedPackages || []}
            getRowId={(pkg) => pkg.prepaidId}
            emptyMessage="You do not have any completed prepaid job packages."
            isLoading={isLoading || isFetching}
          />
        </section>
      </Stack>
    </DashboardPage>
  );
};

export default JobsPrepaid;
